import { noop } from "@/lib/utils";
import type { BasicExtensionFactory, Canvas, Object } from "../types/canvasTypes";
import { isIgnoredEventOnInputTarget } from "../helpers/canvasHelpers";
import { isTextEditingState } from "../helpers/textHelpers";
import { deleteActiveObjects } from "../utils/canvasUtils";
import { addWindowEventListener } from "@/lib/windowUtils";
import { isHistoryProcessing } from "../helpers/canvasCustomPropertiesHelper";
import { KEY_VALUES } from "@/constants/keyConstants";

const NAME = "object-deletion";

export type DeleteActiveObjectFunction = () => void;
export type ObjectDeletingHandler = (object: Object) => void;
export const createObjectDeletionExtension: BasicExtensionFactory<{
  deleteActiveObjectFunctionFactory: (fn: DeleteActiveObjectFunction) => void;
  handleObjectDeleting: ObjectDeletingHandler;
}> = ({ deleteActiveObjectFunctionFactory, handleObjectDeleting }) => {
  let unsubscribe: () => void = noop;
  const deleteActiveObjectsSubscription = createObjectDeletingSubscription(handleObjectDeleting);

  return {
    name: NAME,
    onInit: (canvas) => {
      deleteActiveObjectFunctionFactory(() => deleteActiveObjects(canvas));

      unsubscribe = addWindowEventListener("keyup", (event) => {
        if (!isDeleteOrBackspaceKey(event.key) || isIgnoredEventOnInputTarget(event)) return;

        const activeObject = canvas.getActiveObject();

        // If there is no active object, or we press delete or backspace key in text editing state mode we do nothing
        if (!activeObject || isTextEditingState(activeObject)) return;

        deleteActiveObjects(canvas);
      });
      deleteActiveObjectsSubscription.subscribe(canvas);
    },
    onDestroy: (canvas) => {
      deleteActiveObjectsSubscription.unsubscribe(canvas);
      unsubscribe();
    },
  };
};

function createObjectDeletingSubscription(handler?: ObjectDeletingHandler) {
  let handleRemoving: (event: { target: Object }) => void = noop;

  return {
    subscribe: (canvas: Canvas) => {
      handleRemoving = (event) => {
        if (isHistoryProcessing(canvas)) return;

        handler?.(event.target);
      };

      canvas.on("object:removed", handleRemoving as any);
    },

    unsubscribe: (canvas: Canvas) => canvas.off("object:removed", handleRemoving as any),
  };
}

function isDeleteOrBackspaceKey(key: string) {
  return key === KEY_VALUES.Delete || key === KEY_VALUES.Backspace;
}
